/**
 * BlocksPalette — clickable library of metrics, numbers, operators, functions
 * For Media Devoted | Created By Ciro Autiero
 *
 * Always visible above the formula canvas. Clicking any chip emits one (or
 * more) ChipTokens that BlocksBuilder appends to the canvas. Pass `subformulas`
 * to add a Subformulas section (master-formula context).
 */

import { FC, memo } from 'react';
import type { FormulaSubformula } from '@trafficpulse/shared';
import { makeSubformulaRefName } from '@trafficpulse/shared';

import { useAllowLowest } from '@/features/flow-optimizer/tabs/calculation/sliders/lowest';

import { useFormulaMetricCatalog } from '../metric-catalog/FormulaMetricCatalogContext';
import { ChipToken, makeChip } from './blocks.tokens';
import {
  MetricsRow,
  NumberInsertControls,
  PaletteChip,
  PaletteSection,
  SubformulasRow,
} from './palette';

export interface BlocksPaletteProps {
  onInsert: (chips: ChipToken[]) => void;
  /** When present, adds a Subformulas section with one chip per subformula. */
  subformulas?: FormulaSubformula[];
}

const OPS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '+', label: '+' },
  { value: '-', label: '−' },
  { value: '*', label: '×' },
  { value: '/', label: '÷' },
  { value: '%', label: '%' },
];
const FNS = ['min', 'max', 'abs', 'log', 'sqrt'] as const;
/** The Automation's formulas also offer lowest(metric) — "least is best" (02/11/2026). */
const AUTOMATION_FNS = [...FNS, 'lowest'] as const;
const NUMBER_PRESETS: ReadonlyArray<number> = [0, 1, 0.5, 100];

const BlocksPalette: FC<BlocksPaletteProps> = ({ onInsert, subformulas }) => {
  const metadata = useFormulaMetricCatalog();
  const metrics = metadata.data?.metrics ?? [];
  const fns = useAllowLowest() ? AUTOMATION_FNS : FNS;
  const insertOne = (chip: ChipToken) => onInsert([chip]);
  const insertFn = (fn: string) => onInsert([makeChip('fn', fn), makeChip('lparen', '(')]);
  const insertSubref = (subId: string) =>
    insertOne(makeChip('subref', makeSubformulaRefName(subId)));
  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
      {subformulas && subformulas.length > 0 ? (
        <PaletteSection label="Subformulas">
          <SubformulasRow subformulas={subformulas} onPick={insertSubref} />
        </PaletteSection>
      ) : null}
      <PaletteSection label="Metrics">
        <MetricsRow metrics={metrics} onPick={(id) => insertOne(makeChip('metric', id))} />
      </PaletteSection>
      <PaletteSection label="Numbers">
        <NumberInsertControls onInsert={(value) => insertOne(makeChip('number', value))} />
        {NUMBER_PRESETS.map((n) => (
          <PaletteChip
            key={n}
            tone="number"
            onClick={() => insertOne(makeChip('number', String(n)))}
          >
            {n}
          </PaletteChip>
        ))}
      </PaletteSection>
      <PaletteSection label="Operators">
        {OPS.map((op) => (
          <PaletteChip key={op.value} tone="op" onClick={() => insertOne(makeChip('op', op.value))}>
            {op.label}
          </PaletteChip>
        ))}
        <PaletteChip tone="punct" onClick={() => insertOne(makeChip('lparen', '('))}>
          (
        </PaletteChip>
        <PaletteChip tone="punct" onClick={() => insertOne(makeChip('rparen', ')'))}>
          )
        </PaletteChip>
        <PaletteChip tone="punct" onClick={() => insertOne(makeChip('comma', ','))}>
          ,
        </PaletteChip>
      </PaletteSection>
      <PaletteSection label="Functions">
        {fns.map((fn) => (
          <PaletteChip key={fn} tone="fn" onClick={() => insertFn(fn)}>
            {fn}(
          </PaletteChip>
        ))}
      </PaletteSection>
    </div>
  );
};

export default memo(BlocksPalette);
